import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/upr-api";
import DOMPurify from "dompurify";
import { RICH_TEXT_SANITIZE } from "@/lib/rich-text";

const PAGE_SIZE = 9;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    api.get("/blog", { params: { limit: PAGE_SIZE, offset: 0 } })
      .then(({ data }) => { setPosts(data.items || []); setTotal(data.total || 0); })
      .finally(() => setLoading(false));
  }, []);

  const loadMore = async () => {
    if (loadingMore || posts.length >= total) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/blog", { params: { limit: PAGE_SIZE, offset: posts.length } });
      setPosts((prev) => [...prev, ...(data.items || [])]);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-20" data-testid="blog-page">
      <div className="upr-eyebrow mb-6"><span className="upr-divider"/>Journal</div>
      <h1 className="font-heading font-black tracking-tighter leading-[0.88] text-5xl md:text-7xl lg:text-8xl" style={{ letterSpacing: "-0.035em" }}>
        Blog.
      </h1>
      <p className="mt-5 max-w-xl text-base md:text-lg" style={{ color: "var(--upr-muted)" }}>
        Release announcements, label nights, gigs and notes from the road.
      </p>

      {loading ? (
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(3)].map((_, i) => <div key={i} className="aspect-[4/3] animate-pulse" style={{ background: "var(--upr-surface)" }} />)}
        </div>
      ) : posts.length === 0 ? (
        <div className="mt-12 text-sm" style={{ color: "var(--upr-muted)" }}>No posts yet.</div>
      ) : (
        <>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10" data-testid="blog-grid">
            {posts.map((p) => (
              <Link to={`/blog/${p.slug}`} key={p.id} className="block upr-card-hover" data-testid={`blog-card-${p.id}`}>
                {p.cover_url && (
                  <div className="upr-cover aspect-[4/3]" style={{ border: "1px solid var(--upr-border)" }}>
                    <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="pt-4">
                  <div className="upr-eyebrow" style={{ fontSize: "0.62rem" }}>
                    {new Date(p.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                  <h3 className="font-heading font-extrabold text-xl md:text-2xl leading-tight mt-1" style={{ letterSpacing: "-0.02em" }}>
                    {p.title}
                  </h3>
                  {p.excerpt && (
                    <div
                      className="mt-2 text-sm upr-rich-teaser"
                      style={{ color: "var(--upr-muted)" }}
                      data-testid={`blog-excerpt-${p.id}`}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(p.excerpt, RICH_TEXT_SANITIZE) }}
                    />
                  )}
                  <div className="mt-3 upr-eyebrow">Read more →</div>
                </div>
              </Link>
            ))}
          </div>

          {posts.length < total && (
            <div className="mt-10 flex justify-center" data-testid="blog-load-more-wrap">
              <button
                type="button"
                className="upr-btn"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="blog-load-more"
              >
                {loadingMore ? "Loading…" : `Load more (${total - posts.length} left)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
